/**
 * API Client — общение Mini App с Bot Worker
 *
 * Все запросы идут через initData для авторизации.
 * Base URL настраивается через env (VITE_API_URL).
 */

import { ok, err, type Result } from '@tg-miniapp/shared-types';

// ─── Config ────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL as string || 'http://localhost:8787';

// ─── Core Request ──────────────────────────────────────────

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  initData?: string;
}

async function apiRequest<T>(
  path: string,
  options: ApiOptions = {},
): Promise<Result<T, Error>> {
  const { method = 'GET', body, initData } = options;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (initData) {
      headers['Authorization'] = `tma ${initData}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      return err(new Error(`API error ${response.status}: ${errorText}`));
    }

    const data = await response.json() as T;
    return ok(data);
  } catch (error: unknown) {
    return err(new Error(`Network error: ${error instanceof Error ? error.message : String(error)}`));
  }
}

// ─── Bot Info ──────────────────────────────────────────────

interface BotInfo {
  ok: boolean;
  bot: { id: number; first_name: string; username: string };
}

export async function getBotInfo(): Promise<Result<BotInfo, Error>> {
  return apiRequest<BotInfo>('/api/me');
}

// ─── Health ────────────────────────────────────────────────

interface HealthResponse {
  status: string;
  version: string;
  timestamp: number;
}

export async function getHealth(): Promise<Result<HealthResponse, Error>> {
  return apiRequest<HealthResponse>('/health');
}

// ─── Bookings ──────────────────────────────────────────────

interface BookingsResponse {
  bookings: Array<{
    id: string;
    clientTgId: string;
    slotStart: number;
    slotEnd: number;
    status: string;
    serviceName?: string;
  }>;
}

export async function getBookings(
  initData: string,
  date: string,
): Promise<Result<BookingsResponse, Error>> {
  return apiRequest<BookingsResponse>(`/api/bookings?date=${date}`, { initData });
}

// ─── Config ────────────────────────────────────────────────

import type { BotConfig } from '@tg-miniapp/shared-types';

export async function getBotConfig(
  initData: string,
): Promise<Result<BotConfig, Error>> {
  return apiRequest<BotConfig>('/api/config', { initData });
}

export async function updateBotConfig(
  initData: string,
  config: BotConfig,
): Promise<Result<void, Error>> {
  return apiRequest<void>('/api/config', {
    method: 'PUT',
    body: config,
    initData,
  });
}
